import { stringifyQueryParams } from "providers/queryParams";
import { useCallback } from "react";

import { useQueryParams } from "../../hooks/useQueryParams";
import {
  addOrUpdateKeyInOmnisearch,
  breakApartOmnisearch,
  removeKeyInOmnisearch,
} from "../../utils/omnisearchUtils";

export type Filter = {
  omnisearch: string;
  updateOmnisearch: (filters: [string, string][], clearFirst?: boolean) => void;
  removeOmnisearch: (filter: string) => void;
  getFilter: (filter: string) => string | undefined;
  getOmnisearchUrl: (baseUrl: string, filters: [string, string][]) => string;
};

export default function useOmnisearchFilters(): Filter {
  const { filters, setFilters } = useQueryParams<Filter>();

  const updateOmnisearch = useCallback(
    (filters: [string, string][], clearFirst?: boolean) => {
      setFilters((state) => {
        state.omnisearch = getOmnisearchValueFromFilters(
          filters,
          !clearFirst ? (state.omnisearch ?? "") : ""
        );
      });
    },
    [setFilters]
  );

  const getOmnisearchUrl = useCallback((baseUrl: string, filters: [string, string][]) => {
    const omnisearch = getOmnisearchValueFromFilters(filters);

    return `${baseUrl}?${stringifyQueryParams({
      omnisearch,
    })}`;
  }, []);

  const removeOmnisearch = useCallback(
    (filter: string) => {
      setFilters((state) => {
        state.omnisearch = removeKeyInOmnisearch(state.omnisearch ?? "", filter);
      });
    },
    [setFilters]
  );

  const getFilter = useCallback(
    (filter: string) => {
      const { kvpValues } = breakApartOmnisearch(filters.omnisearch ?? "");
      const kvp = kvpValues.find(([k]) => k === filter);
      return kvp?.[1];
    },
    [filters]
  );

  return {
    omnisearch: filters.omnisearch ?? "",
    updateOmnisearch,
    removeOmnisearch,
    getFilter,
    getOmnisearchUrl,
  };
}

function getOmnisearchValueFromFilters(filters: [string, string][], initialOmnisearch = "") {
  let omnisearch = initialOmnisearch;
  for (const [key, value] of filters) {
    omnisearch = addOrUpdateKeyInOmnisearch(omnisearch, key, value);
  }
  return omnisearch;
}
